import {
  Center,
  Flex,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Link,
  Card,
  CardBody,
} from "@chakra-ui/react";
import LoanHeaderDetail from "components/elements/LoanHeaderDetail";
import PageHeader from "components/layout/PageHeader";
import EditItemDrawer from "components/loans/LoanChecklist/EditChecklistItem/EditItemDrawer";
import { ChecklistConsumer, ChecklistProvider } from "providers/checklist";
import { LoanContext } from "providers/loan";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { stringToDollar } from "utils/helpers";
import ChecklistTab from "./elements/ChecklistTab";
import DocumentsTab from "./elements/DocumentsTab";
import OverviewTab from "./elements/OverviewTab";
import PaymentsTab from "./elements/PaymentsTab";
import { checkDisability, permissionConstants, hasPermission } from "utils/permissionHelper";
import { getCustomCurrency } from "utils/uiconfighelper";
import { ErrorBoundary } from "@sentry/react";
import { FeeModel } from "providers/loans/fee.model";
import colors from "utils/colors";

function Loan() {
  const location = useLocation();
  const navigate = useNavigate();
  let params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const { user } = useTenant();
  const { loan } = useContext(LoanContext);

  const [pageLoading, setPageLoading] = useState(true);
  const [tabs, setTabs] = useState<string[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [budget, setBudget] = useState<number>(0);
  const canEditChecklist = hasPermission(user, permissionConstants.editChecklist);

  const updateBudget = (newBudget: number) => {
    setBudget(newBudget);
  };

  useEffect(() => {
    if (loan) {
      setPageLoading(false);
    }
  }, [loan]);

  useEffect(() => {
    if (loan) {
      let defaultTabs = ["overview", "documents", "checklist"];
      defaultTabs.push("payments");
      setTabs(defaultTabs);
    }
  }, [user.type, loan]);

  useEffect(() => {
    if (tabs.length > 0) {
      const urlIndex = tabs.indexOf(params.get("tab") as string);
      if (urlIndex !== tabIndex) {
        setTabIndex(urlIndex === -1 ? 0 : urlIndex);
      }
      if (checkDisability(user, "disable_payment_section") && user.type === "borrower") {
        const copyTabs = [...tabs];
        const filteredTabs = copyTabs.filter((tab: any) => tab !== "payments");
        setTabs(filteredTabs);
      }
    }
  }, [tabs]);

  useEffect(() => {
    if (tabs.length > 0) {
      const newParams = new URLSearchParams();

      newParams.append("tab", tabs[tabIndex]);
      if (params.get("item")) {
        newParams.append("item", params.get("item") as string);
      }
      if (params.get("draw_request")) {
        newParams.append("draw_request", params.get("draw_request") as string);
      }
      navigate(location.pathname + "?" + newParams.toString());
    }
  }, [tabIndex]);

  const onTabChange = (index: number) => {
    setTabIndex(index);
  };

  if (pageLoading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  if (!loan) {
    return (
      <Center>
        <Text>No loan found.</Text>
      </Center>
    );
  }

  // Calculate total fees and remaining balance
  const totalFees = loan.fees.reduce(
    (acc: number, fee: FeeModel) => acc + (fee.fee_amount || 0),
    0
  );

  const remainingBalance = Number(loan.amount) - budget - totalFees;

  return (
    <>
      <PageHeader
        title={loan.name}
        breadcrumbs={[
          {
            title: "Loans",
            to: "/loans",
          },
          {
            title: loan.name,
          },
        ]}
      />
      <ChecklistProvider>
        <>
          <Card borderBottomRadius="0px">
            <CardBody>
              <Flex justifyContent="space-between" gap={2}>
                {!checkDisability(user, "disable_properties") && (
                  <LoanHeaderDetail
                    label="Property"
                    text={
                      loan?.property?.id ? (
                        <Link href={`/properties/${loan.property.id}`}>
                          <Text color={"black"} fontWeight={500}>
                            {loan.property.name}
                          </Text>
                        </Link>
                      ) : (
                        <Text fontSize={"16px"} color={"blackx"} fontWeight={500}>
                          Not Provided
                        </Text>
                      )
                    }
                  />
                )}

                <LoanHeaderDetail
                  label="Entity"
                  text={loan.property?.entity?.name}
                  link={`/companies/${loan.borrower_company.id}/entities/${loan.property?.entity?.id}`}
                />

                <LoanHeaderDetail label="Loan Number" text={`#${loan.id.split("-")[0]}`} />

                <LoanHeaderDetail
                  label="Borrower"
                  text={`${loan.borrower.first_name} ${loan.borrower.last_name}`}
                />

                <LoanHeaderDetail
                  label="Company"
                  text={loan.borrower_company.name}
                  link={`/companies/${loan.borrower_company.id}`}
                />

                <LoanHeaderDetail label="Loan Type" text={loan.loan_type.title} />

                <LoanHeaderDetail
                  label="Remaining Balance"
                  text={stringToDollar(String(remainingBalance), getCustomCurrency(user))}
                />

                <LoanHeaderDetail
                  align="right"
                  label="Loan Amount"
                  text={
                    loan.amount
                      ? stringToDollar(loan.amount, getCustomCurrency(user))
                      : "Not Provided"
                  }
                />
              </Flex>
            </CardBody>
          </Card>
          <Tabs isFitted variant="unstyled" index={tabIndex} onChange={onTabChange}>
            <TabList bg="white">
              {tabs.map(tab => (
                <Tab
                  data-cy={`loan-tab-${tab}`}
                  key={tab}
                  fontWeight="500"
                  fontSize="14px"
                  borderBottomWidth="3px"
                  borderBottomColor="#E6D7F9"
                  textTransform="capitalize"
                  _selected={{
                    bg: colors.GRAY_50,
                    borderBottomColor: "primary",
                  }}
                  _hover={{
                    bg: colors.GRAY_50,
                  }}
                >
                  {tab}
                </Tab>
              ))}
            </TabList>
            <TabPanels mt="20px">
              <TabPanel p="0px" bg="transparent">
                <OverviewTab />
              </TabPanel>
              <TabPanel p="0px" bg="transparent">
                <DocumentsTab />
              </TabPanel>
              <TabPanel p="0px" bg="transparent">
                <ChecklistTab />
              </TabPanel>
              {tabs.includes("payments") && (
                <TabPanel p="0px" bg="transparent">
                  <PaymentsTab updateBudget={updateBudget} />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>

          <ChecklistConsumer>
            {({ editItem }) => (editItem ? <EditItemDrawer canEdit={canEditChecklist} /> : null)}
          </ChecklistConsumer>
        </>
      </ChecklistProvider>
    </>
  );
}

function LoanFallback() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "1rem",
          backgroundColor: "white",
          borderRadius: "1rem",
          padding: "2rem",
        }}
      >
        <h1
          style={{
            fontSize: "1.5rem",
          }}
        >
          There was an error loading this loan.
        </h1>
        <p>
          We're investigating the issue. You can go back to the loans list by clicking{" "}
          <Link href="/loans">here</Link>.
        </p>
      </div>
    </div>
  );
}
const BoundedLoan = () => (
  <ErrorBoundary fallback={<LoanFallback />}>
    <Loan />
  </ErrorBoundary>
);
export default BoundedLoan;
